import React, { useState } from "react";
import { Camera, Expand } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import Lightbox from "../components/Lightbox";
import SmartImage from "../components/SmartImage";
import { mediaUrl } from "../lib/api";
import {
  CrumbLine,
  CtaPair,
  SiblingPills,
  StatChip,
  useEnquiry,
} from "./parts";

/**
 * GALLERY page - image-led, almost no copy on the source.
 * Full-bleed lead photograph with the title over it, then a true masonry wall
 * (CSS columns) so the varied aspect ratios of the migrated photos are kept
 * instead of being crushed into identical squares.
 */
export default function GalleryTemplate({
  page,
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
}) {
  const [lb, setLb] = useState(null);

  const blocks = page.blocks || [];
  const textBlocks = blocks.filter((b) => b.type !== "image");
  const seen = new Set();
  const shots = [
    ...(page.gallery || []),
    ...blocks.filter((b) => b.type === "image"),
  ].filter((g) => {
    if (!g.url || seen.has(g.url)) return false;
    seen.add(g.url);
    return true;
  });

  const lead = page.hero_image || shots[0]?.url;
  const wall = shots.filter((s) => s.url !== lead);
  const to = useEnquiry(page, lead, parentLabel);
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;
  const urls = wall.map((s) => s.url);

  return (
    <div data-testid="template-gallery">
      {/* full-bleed lead */}
      <section className="relative" data-testid="gallery-lead">
        <div className="relative h-[46vh] min-h-[320px] w-full overflow-hidden md:h-[62vh]">
          {lead ? (
            <img
              src={mediaUrl(lead, 1200)}
              alt={heading}
              className="h-full w-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="h-full w-full" style={{ background: "var(--nq-ink)" }} />
          )}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(11,13,12,0.96) 8%, rgba(11,13,12,0.28) 55%, rgba(11,13,12,0.6))" }}
          />
          <div className="noise-overlay" />
        </div>

        <div className="container-nq relative -mt-40 pb-2 md:-mt-52">
          <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
          <h1
            className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-6xl"
            data-testid="page-title"
          >
            {heading}
          </h1>
          {page.meta_description && (
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
              {page.meta_description}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <CtaPair to={to} label="Enquire about a build" phone={phone} />
            {shots.length > 0 && <StatChip value={shots.length} label="Photos" />}
          </div>
        </div>
      </section>

      <div className="container-nq section-nq space-y-12">
        {textBlocks.length > 0 && (
          <div className="max-w-3xl">
            <BlockRenderer blocks={textBlocks} />
          </div>
        )}

        {wall.length > 0 && (
          <section>
            <div className="mb-6 flex items-center gap-2">
              <Camera className="h-4 w-4 gold-text" />
              <p className="eyebrow">The gallery</p>
            </div>
            <div
              className="columns-2 gap-4 md:columns-3 xl:columns-4 [&>*]:mb-4"
              data-testid="gallery-grid"
            >
              {wall.map((im, i) => (
                <button
                  key={im.url}
                  type="button"
                  onClick={() => setLb(i)}
                  className="card-nq group relative block w-full cursor-zoom-in break-inside-avoid focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)]"
                  data-testid="gallery-item"
                  aria-label={`Open photo ${i + 1}`}
                >
                  <SmartImage src={im.url} alt={im.alt || heading} ratio="aspect-auto" fit="cover" w={1200} />
                  <span
                    className="pointer-events-none absolute right-3 top-3 rounded-md p-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    style={{ background: "rgba(11,13,12,0.72)" }}
                  >
                    <Expand className="h-3.5 w-3.5 gold-text" />
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        <SiblingPills siblings={siblings} />
      </div>

      <Lightbox images={urls} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
